import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, Menu, MenuItem, Paper, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  addRoleAccounts, dismissRole, listRoleStatuses, listStackSetsAccounts, removeRole, retryRole,
} from '../../../services/stacksetsApi';
import { POLL_INTERVAL_MS } from '../securityhub/pollInterval';
import { formatStatusLabel } from '../../../utils/statusLabel';
import { PortalLoading } from '../../../components/portal/PortalStates';
import {
  CK, FONT_BODY, statusChipSx, intentFromColor, tableHeadRowSx, headCellSx, rowSx,
  bodyCellSx, listCardSx, sectionHeadingSx, emptyTextSx, splitRowSx, actionRowSx,
  dialogProps, dialogTitleSx,
} from '../portalStyles';
import ExecutionRoleAccountPicker, { JIT_ROLE_HELPER_TEXT } from './ExecutionRoleAccountPicker';

const phaseColor = (p) => ({
  provisioned: 'success',
  failed: 'error',
  pending: 'default',
  provisioning: 'info',
  removing: 'warning',
  removed: 'default',
}[p] || 'default');

const TRANSIENT_PHASES = ['pending', 'provisioning', 'removing'];

// Filter chips. Labels are status tokens, run through formatStatusLabel like
// every other status label.
const FILTERS = [
  { key: 'all', match: () => true },
  { key: 'provisioned', match: (r) => r.phase === 'provisioned' },
  { key: 'failed', match: (r) => r.phase === 'failed' },
  { key: 'in_progress', match: (r) => TRANSIENT_PHASES.includes(r.phase) },
];

const BULK_CONCURRENCY = 5;

// runPool calls fn on every item with at most `limit` calls in flight and
// returns the failures as [{item, error}].
async function runPool(items, limit, fn) {
  const failures = [];
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const item = items[next];
      next += 1;
      try {
        // eslint-disable-next-line no-await-in-loop
        await fn(item);
      } catch (e) {
        failures.push({ item, error: e.message });
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return failures;
}

const CONFIRM_COPY = {
  remove: {
    title: 'Remove execution role',
    body: 'Prism deletes CKPrism-StackSetExecutionRole in this account and stops tracking it. If a StackSet deploys here later, the role is created again automatically.',
    action: 'Remove role',
  },
  dismiss: {
    title: 'Dismiss execution role',
    body: 'Prism stops tracking this account without touching AWS. If CKPrism-StackSetExecutionRole is already in the account, it stays there and may still have AdministratorAccess attached; delete it yourself if it is no longer needed. Use this for accounts Prism can no longer reach.',
    action: 'Dismiss',
  },
  dismissAll: {
    title: 'Dismiss all failed roles',
    body: 'Prism stops tracking every failed account without touching AWS. If CKPrism-StackSetExecutionRole is already in those accounts, it stays there and may still have AdministratorAccess attached; delete it yourself if it is no longer needed.',
    action: 'Dismiss all',
  },
};

// RoleStatusPanel shows per-account execution-role status with filter chips,
// per-row actions (Retry / Remove / Dismiss), bulk actions on failed rows, and
// Add accounts. Accounts without a row get their role just in time at their
// first deploy. showRetry=false hides every action (the in_progress transition
// screen, where the backend rejects changes until the fan-out finishes).
export default function RoleStatusPanel({ adminAccountId, onQuiesce, showRetry = true }) {
  const [rows, setRows] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(() => new Set());
  const [filter, setFilter] = useState(null); // null until the first load picks a default
  const [menu, setMenu] = useState(null); // { anchor, row }
  const [confirm, setConfirm] = useState(null); // { kind, accountId? }
  const [bulkRunning, setBulkRunning] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addSelected, setAddSelected] = useState(() => new Set());
  const [adding, setAdding] = useState(false);
  const pollRef = useRef(null);
  // onQuiesce is typically an inline closure from the parent; keep the latest
  // in a ref so the long-lived poll tick never calls a stale one.
  const onQuiesceRef = useRef(onQuiesce);
  useEffect(() => { onQuiesceRef.current = onQuiesce; });

  const refresh = useCallback(async () => {
    try {
      const res = await listRoleStatuses();
      setRows(res?.data || []);
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Default filter, chosen once: Failed when there are failures, else All.
  useEffect(() => {
    if (rows && filter === null) {
      setFilter(rows.some((r) => r.phase === 'failed') ? 'failed' : 'all');
    }
  }, [rows, filter]);

  // Accounts are best-effort decoration for names, and the Add accounts
  // source — never blank the grid when the accounts endpoint fails.
  useEffect(() => {
    (async () => {
      try {
        const res = await listStackSetsAccounts();
        // Backend returns a bare slice of {accountId, name, status}; read
        // defensively so a wrapped/snake_case shape still works.
        const list = res?.data?.accounts || res?.data || [];
        setAccounts(list
          .map((a) => ({ accountId: a.accountId || a.account_id, name: a.name || a.accountName || '' }))
          .filter((a) => a.accountId));
      } catch (_) {
        setAccounts([]);
      }
    })();
  }, []);

  const names = useMemo(() => {
    const map = {};
    accounts.forEach((a) => { map[a.accountId] = a.name; });
    return map;
  }, [accounts]);

  // Poll while any role is transitioning (StackSetsList pattern): keyed on a
  // derived boolean primitive, so an action that flips a row to a transient
  // phase after mount re-arms the poll. The interval stops itself inside the
  // tick only when a SUCCESSFUL response confirms quiescence — a failed fetch
  // keeps polling. Deliberately no cleanup in this effect: setRows on each
  // tick would otherwise destroy-and-recreate the interval; a separate
  // unmount-only effect handles cleanup.
  const hasTransient = !!rows?.some((r) => TRANSIENT_PHASES.includes(r.phase));
  useEffect(() => {
    if (!hasTransient || pollRef.current) return;
    pollRef.current = setInterval(async () => {
      const res = await listRoleStatuses().catch(() => null);
      if (!res) return; // transient error — keep polling
      const next = res?.data || [];
      setRows(next);
      const anyTransient = next.some((r) => TRANSIENT_PHASES.includes(r.phase));
      if (!anyTransient && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        // Settled — let the parent refresh config so the rollup (e.g.
        // partial_failure → enabled) is reflected without a reload.
        onQuiesceRef.current?.();
      }
    }, POLL_INTERVAL_MS);
  }, [hasTransient]);

  // Stop the interval when the component unmounts.
  useEffect(() => {
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, []);

  const markBusy = (id, on) => setBusy((cur) => {
    const next = new Set(cur);
    if (on) next.add(id); else next.delete(id);
    return next;
  });

  // runOne wraps a per-account action: busy flag, error surface, refetch so
  // hasTransient re-arms the poll, and a config refresh for synchronous
  // changes (dismiss) that never pass through a transient phase.
  const runOne = async (accountId, fn, { syncChange = false } = {}) => {
    markBusy(accountId, true);
    setError(null);
    setNotice(null);
    try {
      await fn(accountId);
      await refresh();
      if (syncChange) onQuiesceRef.current?.();
    } catch (e) {
      setError(e.message);
    } finally {
      markBusy(accountId, false);
    }
  };

  const runBulk = async (fn, label, { syncChange = false } = {}) => {
    const targets = (rows || []).filter((r) => r.phase === 'failed').map((r) => r.accountId);
    if (targets.length === 0) return;
    setBulkRunning(true);
    setError(null);
    setNotice(null);
    const failures = await runPool(targets, BULK_CONCURRENCY, fn);
    await refresh();
    if (syncChange) onQuiesceRef.current?.();
    setBulkRunning(false);
    if (failures.length > 0) {
      setError(`${label}: ${failures.length} of ${targets.length} failed. ${failures.slice(0, 3).map((f) => `${f.item}: ${f.error}`).join('; ')}${failures.length > 3 ? '…' : ''}`);
    } else {
      setNotice(`${label}: ${targets.length} account${targets.length === 1 ? '' : 's'} done.`);
    }
  };

  const onConfirm = async () => {
    const c = confirm;
    setConfirm(null);
    if (!c) return;
    if (c.kind === 'remove') await runOne(c.accountId, removeRole);
    else if (c.kind === 'dismiss') await runOne(c.accountId, dismissRole, { syncChange: true });
    else if (c.kind === 'dismissAll') await runBulk(dismissRole, 'Dismiss all failed', { syncChange: true });
  };

  const rowIds = useMemo(() => new Set((rows || []).map((r) => r.accountId)), [rows]);
  const addCandidates = useMemo(() => accounts.filter((a) => !rowIds.has(a.accountId)), [accounts, rowIds]);

  const openAdd = () => {
    setAddSelected(new Set());
    setAddOpen(true);
  };
  const submitAdd = async () => {
    setAdding(true);
    setError(null);
    setNotice(null);
    try {
      await addRoleAccounts([...addSelected]);
      setAddOpen(false);
      await refresh();
    } catch (e) {
      setError(e.message);
    } finally {
      setAdding(false);
    }
  };

  const acctLabel = (id) => (names[id] ? `${names[id]} (${id})` : id);
  const all = rows || [];
  const counts = Object.fromEntries(FILTERS.map((f) => [f.key, all.filter(f.match).length]));
  const activeFilter = FILTERS.find((f) => f.key === filter) || FILTERS[0];
  const shown = all.filter(activeFilter.match);
  const provisioned = counts.provisioned;
  const failed = counts.failed;

  const menuRow = menu?.row;
  const canRemove = (r) => r.phase === 'provisioned' || r.phase === 'failed';
  const hasActions = (r) => r.phase === 'failed' || canRemove(r);

  return (
    <Paper elevation={0} sx={listCardSx}>
      <Box sx={{ px: 3, pt: 3, pb: 2 }}>
        <Box sx={{ ...splitRowSx, mb: 1 }}>
          <Typography sx={sectionHeadingSx}>Execution roles</Typography>
          {rows && (
            <Box sx={{ ...actionRowSx, gap: 1 }}>
              <Chip size="small" label={`${provisioned}/${rows.length} provisioned`} sx={statusChipSx(intentFromColor(provisioned === rows.length ? 'success' : 'default'))} />
              {failed > 0 && <Chip size="small" label={`${failed} failed`} sx={statusChipSx('error')} />}
              {showRetry && (
                <Button size="small" variant="outlined" onClick={openAdd}>
                  Add accounts
                </Button>
              )}
            </Box>
          )}
        </Box>
        <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body }}>
          CKPrism-StackSetExecutionRole in the accounts below. {JIT_ROLE_HELPER_TEXT}
        </Typography>
        {error && <Alert severity="error" sx={{ mt: 2, borderRadius: '8px' }} onClose={() => setError(null)}>{error}</Alert>}
        {notice && <Alert severity="success" sx={{ mt: 2, borderRadius: '8px' }} onClose={() => setNotice(null)}>{notice}</Alert>}
        {rows && rows.length > 0 && (
          <Box sx={{ ...actionRowSx, mt: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {FILTERS.map((f) => (
                <Chip
                  key={f.key}
                  size="small"
                  label={`${formatStatusLabel(f.key)} (${counts[f.key]})`}
                  onClick={() => setFilter(f.key)}
                  sx={{
                    ...statusChipSx(activeFilter.key === f.key ? 'info' : 'neutral'),
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease, border-color 0.2s ease',
                  }}
                />
              ))}
            </Box>
            {showRetry && activeFilter.key === 'failed' && failed > 0 && (
              <Box sx={{ ...actionRowSx, gap: 1, ml: 'auto' }}>
                {bulkRunning && <CircularProgress size={16} thickness={5} sx={{ color: CK.blue }} />}
                <Button size="small" color="warning" disabled={bulkRunning} onClick={() => runBulk(retryRole, 'Retry all failed')}>
                  Retry all failed
                </Button>
                <Button size="small" disabled={bulkRunning} onClick={() => setConfirm({ kind: 'dismissAll' })}>
                  Dismiss all failed
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Box>
      {!rows ? (
        <PortalLoading label="Loading execution roles…" py={5} />
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={tableHeadRowSx}>
                <TableCell sx={headCellSx}>Account</TableCell>
                <TableCell sx={headCellSx}>Status</TableCell>
                <TableCell sx={{ ...headCellSx, textAlign: 'right' }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map((r, index) => (
                <TableRow key={r.accountId} hover sx={rowSx(index)}>
                  <TableCell sx={bodyCellSx}>
                    {acctLabel(r.accountId)}
                    {r.accountId === adminAccountId && <Chip label="admin" size="small" sx={{ ...statusChipSx('purple'), ml: 1 }} />}
                  </TableCell>
                  <TableCell sx={{ ...bodyCellSx, maxWidth: 420 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip label={formatStatusLabel(r.phase)} size="small" sx={statusChipSx(intentFromColor(phaseColor(r.phase)))} />
                      {(TRANSIENT_PHASES.includes(r.phase) || busy.has(r.accountId)) && <CircularProgress size={14} thickness={5} sx={{ color: CK.blue }} />}
                    </Stack>
                    {r.phase === 'failed' && r.errorMessage && (
                      <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.75rem', color: CK.red, display: 'block', mt: 0.5, wordBreak: 'break-word' }}>
                        {r.errorMessage}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={bodyCellSx} align="right">
                    {showRetry && hasActions(r) && (
                      <IconButton
                        size="small"
                        aria-label={`Actions for ${r.accountId}`}
                        disabled={busy.has(r.accountId) || bulkRunning}
                        onClick={(e) => setMenu({ anchor: e.currentTarget, row: r })}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {shown.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                    <Typography sx={emptyTextSx}>
                      {all.length === 0
                        ? 'No execution roles yet. Add accounts to create the role now, or let it be created at the first deploy.'
                        : 'No accounts match this filter.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Menu anchorEl={menu?.anchor} open={!!menu} onClose={() => setMenu(null)}>
        {menuRow?.phase === 'failed' && (
          <MenuItem onClick={() => { setMenu(null); runOne(menuRow.accountId, retryRole); }}>Retry</MenuItem>
        )}
        {menuRow && canRemove(menuRow) && (
          <MenuItem onClick={() => { setMenu(null); setConfirm({ kind: 'remove', accountId: menuRow.accountId }); }}>Remove</MenuItem>
        )}
        {menuRow?.phase === 'failed' && (
          <MenuItem onClick={() => { setMenu(null); setConfirm({ kind: 'dismiss', accountId: menuRow.accountId }); }}>Dismiss</MenuItem>
        )}
      </Menu>

      <Dialog open={!!confirm} onClose={() => setConfirm(null)} {...dialogProps}>
        <DialogTitle sx={dialogTitleSx}>{confirm ? CONFIRM_COPY[confirm.kind].title : ''}</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {confirm?.accountId && (
            <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 600, color: CK.navy, mt: 1, mb: 1 }}>
              {acctLabel(confirm.accountId)}
            </Typography>
          )}
          {confirm?.kind === 'dismissAll' && (
            <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 600, color: CK.navy, mt: 1, mb: 1 }}>
              {failed} failed account{failed === 1 ? '' : 's'}
            </Typography>
          )}
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body }}>
            {confirm ? CONFIRM_COPY[confirm.kind].body : ''}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button
            variant="contained"
            color={confirm?.kind === 'remove' ? 'error' : 'primary'}
            onClick={onConfirm}
          >
            {confirm ? CONFIRM_COPY[confirm.kind].action : ''}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addOpen} onClose={() => !adding && setAddOpen(false)} {...dialogProps} maxWidth="md">
        <DialogTitle sx={dialogTitleSx}>Add accounts</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body, mt: 1, mb: 0.5 }}>
            Create CKPrism-StackSetExecutionRole now in the accounts you select.
          </Typography>
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.muted, mb: 2 }}>
            {JIT_ROLE_HELPER_TEXT}
          </Typography>
          <ExecutionRoleAccountPicker
            accounts={addCandidates}
            selected={addSelected}
            onChange={setAddSelected}
            adminAccountId={adminAccountId}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setAddOpen(false)} disabled={adding}>Cancel</Button>
          <Button variant="contained" disabled={adding || addSelected.size === 0} onClick={submitAdd}>
            {adding && 'Adding…'}
            {!adding && addSelected.size === 0 && 'Add accounts'}
            {!adding && addSelected.size > 0 && `Add ${addSelected.size} account${addSelected.size === 1 ? '' : 's'}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
